import { useCallback, useEffect, useRef, useState } from 'react';

import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import RoisBackgroundLocation from '../../../../plugins/roisBackgroundLocation';
import { isNativeApp } from '../../../../utils/positionSource';
import { permissionApiDecides } from '../../../../utils/webPermissions';
import { usableFix } from '../../../../utils/exif/captureExif';

import { PHOTO_GEOTAG_FLAG } from '../../../../utils/photoGeotagFlag';

// Se reexporta: la cámara y el `TimerOverlay` lo importan desde aquí.
export { PHOTO_GEOTAG_FLAG };

/**
 * `maximumAge` de 10 s deja que la PRIMERA lectura salga de la caché del
 * sistema al instante; lo que protege de una lectura vieja no es esto sino la
 * comprobación de edad al disparar (`usableFix`).
 */
const WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 10_000,
  timeout: 30_000,
};

/** Código 1 de la Geolocation API: permiso denegado. */
const PERMISSION_DENIED = 1;

/**
 * Cuánto se espera una posición buena antes de avisar «sin señal». Al abrir
 * la cámara la primera lectura suele llegar en milisegundos (caché del
 * sistema); avisar antes haría parpadear el aviso en cada apertura.
 */
export const NO_SIGNAL_AFTER_MS = 5_000;

/** Cada cuánto se reevalúa: una lectura caduca sin que llegue ningún evento. */
const STATUS_TICK_MS = 1_000;

/**
 * En el navegador, la ubicación se pide al abrir la cámara SOLO la primera vez
 * (spec § 15). Si el operario cerró el aviso sin decidir, no se insiste: la
 * cámara le avisa de que esas fotos no llevarán ubicación.
 */
export const LOCATION_ASKED_KEY = 'rois.photoGeotag.locationAsked';

/**
 * Qué va a pasar con la ubicación de la PRÓXIMA foto, para el aviso de la
 * cámara (spec § 15). Nunca frena el disparo.
 *  - `off`: sin flag; no hay nada que avisar.
 *  - `searching`: esperando la primera lectura (no se avisa todavía).
 *  - `ok`: hay una lectura buena; la foto llevará GPS.
 *  - `no-permission`: APK sin la ubicación precisa (la pide el modal
 *    obligatorio; aquí solo se avisa).
 *  - `askable`: web, sin decidir y ya preguntado una vez. No se insiste solo,
 *    pero el aviso lleva un botón para volver a pedirla (`askLocation`).
 *  - `blocked`: web, bloqueada en el navegador. Ya no se puede preguntar: el
 *    aviso dice dónde se desbloquea (el candado de la barra de direcciones).
 *  - `no-signal`: con permiso, pero sin lectura buena (túnel, interior,
 *    ubicación del sistema apagada, precisión insuficiente).
 */
export type CaptureLocationStatus =
  | 'off'
  | 'searching'
  | 'ok'
  | 'no-permission'
  | 'askable'
  | 'blocked'
  | 'no-signal';

/**
 * Si al pedirla desde el botón Chrome contesta «denegado» antes de esto, no
 * enseñó ningún aviso (bloqueo automático tras varios rechazos): se trata
 * como bloqueada y se explica el candado.
 */
const NO_PROMPT_BELOW_MS = 700;

type PermissionAnswer = {
  state: 'granted' | 'denied' | 'prompt' | 'unknown';
  /** Web: para enterarse si lo concede desde el candado con la cámara abierta. */
  webStatus?: PermissionStatus;
};

/**
 * ¿Hay permiso? Nunca pregunta.
 *
 * En la APK la Permissions API del WebView no significa nada (Capacitor
 * concede el origen sin retenerlo, ver `webPermissions.ts`): quien sabe es el
 * plugin nativo, y la cámara NUNCA pide: de eso se encarga el modal obligatorio
 * (`usePhotoPermissionsGate`). Sin la precisa, el plugin no da `granted`.
 */
async function readPermission(): Promise<PermissionAnswer> {
  try {
    if (isNativeApp()) {
      const status = await RoisBackgroundLocation.checkPermissions();
      return { state: status?.location === 'granted' ? 'granted' : 'denied' };
    }
    if (!permissionApiDecides() || !navigator.permissions?.query) {
      return { state: 'unknown' };
    }
    const webStatus = await navigator.permissions.query({
      name: 'geolocation' as PermissionName,
    });
    const state = webStatus.state === 'granted' || webStatus.state === 'denied'
      ? webStatus.state
      : 'prompt';
    return { state, webStatus };
  } catch {
    // Ante la duda, sin GPS y con el aviso: la foto sale igual.
    return { state: isNativeApp() ? 'denied' : 'unknown' };
  }
}

function alreadyAsked(): boolean {
  try {
    return window.localStorage.getItem(LOCATION_ASKED_KEY) === '1';
  } catch {
    return false;
  }
}

function markAsked(): void {
  try {
    window.localStorage.setItem(LOCATION_ASKED_KEY, '1');
  } catch {
    // Sin almacenamiento (modo privado): se volverá a preguntar, nada más.
  }
}

/**
 * Posición para geoetiquetar las fotos de la cámara, SIN hacer esperar al
 * obturador nunca (D3), y su estado para el aviso de la cámara (§ 15).
 *
 * Mientras la cámara está abierta y visible se mantiene un `watchPosition` y
 * se guarda la última lectura; el disparo solo la lee. Pedir una posición por
 * foto bloquearía el disparo justo cuando no hay fix (arranque en frío, salida
 * de un túnel: de 5 a 60 s) o devolvería una lectura vieja en silencio. Es lo
 * que hace Open Camera.
 *
 * ─── Cuándo pregunta (§ 15, cambia D5) ─────────────────────────────────────
 *
 * - **APK**: nunca. La ubicación precisa la exige el modal obligatorio al
 *   entrar; aquí solo se mira.
 * - **Web**: la primera vez que se abre la cámara, y solo cuando la cámara ya
 *   se ve (`ready`), para no sacar el aviso de ubicación a la vez que el de la
 *   cámara. Si ya se preguntó y no se concedió, no se insiste.
 *
 * No se usa `positionSource.watch()`: en la APK arranca el servicio en primer
 * plano del recorrido y exige un `routeId`.
 *
 * La batería solo se gasta mientras la cámara está abierta: en segundo plano
 * el navegador ni siquiera entrega posiciones, así que se para y se rearma al
 * volver.
 */
export function useCaptureLocation({ ready }: { ready: boolean }): {
  currentFix: () => GeolocationPosition | null;
  locationStatus: CaptureLocationStatus;
  /** Web, estado `askable`: vuelve a pedir la ubicación. Solo desde un botón. */
  askLocation: () => void;
} {
  const enabled = useFeatureFlag(PHOTO_GEOTAG_FLAG);
  const lastFix = useRef<GeolocationPosition | null>(null);
  // En ref porque `currentFix` es estable y lo lee el disparo, no el render.
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const readyRef = useRef(ready);
  readyRef.current = ready;
  // Lo que hay que hacer cuando la cámara ya se ve (solo web, en `prompt`).
  const whenReady = useRef<(() => void) | null>(null);
  // Lo que hace el botón «Activar ubicación» (solo web, en `askable`).
  const askRef = useRef<(() => void) | null>(null);
  const [locationStatus, setLocationStatus] = useState<CaptureLocationStatus>('off');

  useEffect(() => {
    if (!enabled || typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationStatus('off');
      return;
    }

    let unmounted = false;
    let blocked = false;
    // Por qué no hay ubicación en web: sin decidir (se puede pedir con el
    // botón) o bloqueada en el navegador.
    let refusal: 'no-permission' | 'askable' | 'blocked' = 'no-permission';
    let askedAt = 0;
    let mayWatch = false;
    let watchId: number | null = null;
    let watchStartedAt = 0;
    let tick: ReturnType<typeof setInterval> | null = null;
    let webStatus: PermissionStatus | undefined;

    const set = (next: CaptureLocationStatus) => {
      if (!unmounted) setLocationStatus(next);
    };

    const evaluate = () => {
      if (blocked) {
        set(refusal);
        return;
      }
      if (watchId === null) return;
      if (usableFix(lastFix.current, Date.now())) {
        set('ok');
        return;
      }
      set(Date.now() - watchStartedAt >= NO_SIGNAL_AFTER_MS ? 'no-signal' : 'searching');
    };

    const stop = () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
        watchId = null;
      }
      if (tick !== null) {
        clearInterval(tick);
        tick = null;
      }
    };

    const start = () => {
      if (unmounted || blocked || !mayWatch || watchId !== null) return;
      if (document.visibilityState === 'hidden') return;
      watchStartedAt = Date.now();
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          lastFix.current = position;
          evaluate();
        },
        (error) => {
          // Sin señal o timeout es lo normal en un túnel: el watch sigue por si
          // llega. Solo un permiso revocado lo apaga, y ya no se insiste.
          if (error.code === PERMISSION_DENIED) {
            blocked = true;
            refusal = webRefusal(Date.now() - askedAt < NO_PROMPT_BELOW_MS);
            stop();
            evaluate();
          }
        },
        WATCH_OPTIONS,
      );
      tick = setInterval(evaluate, STATUS_TICK_MS);
      evaluate();
    };

    const allow = () => {
      blocked = false;
      mayWatch = true;
      start();
    };

    const refuse = () => {
      mayWatch = false;
      blocked = true;
      refusal = webRefusal(false);
      stop();
      evaluate();
    };

    /**
     * En web: bloqueada si el navegador lo dice (o si contestó sin enseñar
     * aviso); si no, sin decidir y se puede volver a pedir. En la APK, el
     * motivo genérico: el modal obligatorio se encarga.
     */
    function webRefusal(answeredWithoutPrompt: boolean): typeof refusal {
      if (isNativeApp()) return 'no-permission';
      if (answeredWithoutPrompt || webStatus?.state === 'denied') return 'blocked';
      return webStatus ? 'askable' : 'no-permission';
    }

    const ask = () => {
      markAsked();
      askedAt = Date.now();
      allow();
    };
    askRef.current = () => {
      if (refusal === 'askable') ask();
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') stop();
      else start();
    };

    set('searching');
    document.addEventListener('visibilitychange', onVisibilityChange);

    void readPermission().then((answer) => {
      if (unmounted) return;
      webStatus = answer.webStatus;
      if (webStatus) {
        // Si lo concede (o lo quita) desde el candado con la cámara abierta.
        webStatus.onchange = () => {
          if (webStatus?.state === 'granted') allow();
          else if (webStatus?.state === 'denied') refuse();
          else if (blocked) {
            // «Restablecer permisos» en Chrome: vuelve a estar sin decidir y
            // el aviso vuelve a ofrecer el botón.
            refusal = 'askable';
            evaluate();
          }
        };
      }

      if (answer.state === 'granted') {
        allow();
      } else if (answer.state === 'denied') {
        refuse();
      } else if (answer.state === 'prompt' && alreadyAsked()) {
        // Ya se le preguntó y no lo concedió: no se insiste.
        refuse();
      } else {
        // Web en `prompt` la primera vez, o sin Permissions API: se arranca el
        // watch —que es lo que saca el aviso del navegador— cuando la cámara
        // ya se ve.
        const first = () => {
          if (answer.state === 'prompt') ask();
          else allow();
        };
        if (readyRef.current) first();
        else whenReady.current = first;
      }
    });

    return () => {
      unmounted = true;
      stop();
      whenReady.current = null;
      askRef.current = null;
      if (webStatus) webStatus.onchange = null;
      // 🔴 Al apagarse el flag (o cerrar la cámara) se OLVIDA la última
      // lectura: seguiría siendo «válida» hasta 120 s y D13 es la puerta legal.
      lastFix.current = null;
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled]);

  useEffect(() => {
    if (!ready || !whenReady.current) return;
    const ask = whenReady.current;
    whenReady.current = null;
    ask();
  }, [ready]);

  const currentFix = useCallback(
    () => (enabledRef.current ? lastFix.current : null),
    [],
  );
  const askLocation = useCallback(() => askRef.current?.(), []);
  return { currentFix, locationStatus, askLocation };
}
